/*

 - actions to start with an action verb - getSomething, setSomething, doSomething - prepares what to send to mutation
 - mutations to start with word mutate - mutateSomething - must simply set what it receives to the state, no business logic
 - getters and state to have no prefix and to be as clear as possible - something{}, somethings[]

*/
import cloneDeep from 'lodash.clonedeep'
import merge from 'lodash.merge'
import isFinite from 'lodash.isfinite'
import get from 'lodash.get'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import isEmpty from 'lodash.isempty'
import { Settings } from 'luxon'
import Highcharts from 'highcharts'
// import { enable as enableDarkMode, setFetchMethod as setDarkReaderFetchMethod, exportGeneratedCSS } from 'darkreader'
import api from '@/api'
import { guid, prepareItemsForWiskInput, compareNumbers, arrayToObjectById, setTimeoutAsync, venueIdToUrl, downloadObjectAsJson } from '@/modules/utils'
import { setPreferences, getPreferences } from '@/modules/storage'
import { initDB, cleanupDB, liveQueryHandler } from '@/modules/db'
import translations from '@/modules/translations'
import infoTooltipsById from '@/modules/infoTooltips'
import { setHighchartsTheme } from '@/modules/highcharts.js'
import { logEvent, extractErrorMetadata } from '@/modules/logger'
import { subscribeToGlobalSettings } from '@/appsync'
import { authSignOut } from '@/auth'
import { getStockAppBaseUrl } from '@/modules/subdomainAuth'
import { featureFlags } from '@/modules/featureFlags'
import { checkCognitoSession } from '@/cognito/authListener'
import router from '@/router'
import gridViewsCore from '@/components/grids/views'

let modalCounter = 0,
  getGlobalOptionsUnsubscribe = null,
  currentVenueId = null,
  debug = 0

// setDarkReaderFetchMethod(window.fetch)
// window.exportGeneratedCSS = async () => {
//   const css = await exportGeneratedCSS()
//   const blob = new Blob([css], { type: 'text/css' })
//   const url = URL.createObjectURL(blob)
//   const link = document.createElement('a')
//   link.href = url
//   link.download = 'dark-reader.css'
//   document.body.appendChild(link)
//   link.click()
//   document.body.removeChild(link)
//   URL.revokeObjectURL(url)
// }

// setTimeout(() => {
//   enableDarkMode()
// }, 5000)

const redirectToSignin = () => {
  let authPages = ['signup', 'signin', 'reset-password', 'deep-link', 'public', 'logout'],
    found = 0

  authPages.forEach(path => {
    if (window.location.pathname.includes(path)) {
      found++
    }
  })
  // Match /auth and all /auth/* sub-routes (logout, redirect, etc.)
  if (window.location.pathname.startsWith('/auth')) found++
  // The OAuth consent page has its own sign-in form; sending it to /signin drops the authorization request
  if (window.location.pathname.startsWith('/oauth/')) found++
  if (!found) {
    let currentRoute = (router && router.currentRoute && router.currentRoute.value) || {},
      path = currentRoute.path || ''

    if (path && path.includes('logout')) {
      path = ''
    }
    let query = { ...currentRoute.query },
      url = '/signin'

    if (path && path !== '/') {
      if (!query.stockPath) { // stockPath handles the post-login redirect instead
        query.returnUrl = path
      }
    }

    Object.keys(query).forEach((key, index) => {
      url += index ? '&' : '?'
      url += `${key}=${query[key]}`
    })

    window.location.href = url
  }
}

const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)'),
  linkElementId = 'dark-mode'

const applyDarkMode = () => {
  if (!document.getElementById(linkElementId)) {
    let linkElement = document.createElement('link')
    linkElement.rel = 'stylesheet'
    linkElement.id = linkElementId
    linkElement.href = '/dark-reader.css'
    document.body.appendChild(linkElement)

    document.getElementById('app').classList.remove('light-mode')
    document.querySelectorAll('.app').forEach(element => {
      element.classList.remove('light-mode')
    })

    document.getElementById('app').classList.add('dark-mode')
    setHighchartsTheme()
  }
}

const applyLightMode = () => {
  const linkElement = document.getElementById(linkElementId)
  if (linkElement) {
    linkElement.remove()
    document.getElementById('app').classList.remove('dark-mode')
    document.querySelectorAll('.app').forEach(element => {
      element.classList.remove('dark-mode')
    })

    document.getElementById('app').classList.add('light-mode')

    setHighchartsTheme()
  }
}

const handleColorSchemeChange = event => {
  event.matches ? applyDarkMode() : applyLightMode()
}

const runAuthenticateFlow = (dispatch, state, authUser) => {
  if (!authUser) {
    redirectToSignin()
    return Promise.reject(new Error('No auth user'))
  }
  dispatch('setFirebaseUser', authUser)
  return api
    .authenticate(state.waitingForVenue, 'cognito session check')
    .then(wiskUser => {
      if (wiskUser) {
        if (wiskUser?.venue?.id && !state.signupFlow) {
          dispatch('setCurrentVenue', { venue: wiskUser.venue, caller: 'authenticate wiskUser.venue' })
        } else {
          dispatch('setCurrentVenue', { venue: { id: 0, subscription: {} }, caller: 'authenticate no venue yet' })
          setTimeout(() => {
            dispatch('setLoadingScreenVisible', false)
          }, 500)
        }
      }
      dispatch('setCurrentUser', wiskUser)
      dispatch('handleReturnUrl')
    })
    .catch(error => {
      console.log('error', error)
      redirectToSignin()
      return Promise.reject(error)
    })
}

const setupAuthEvents = (dispatch, state) => {
  dispatch('setLoadingScreenVisible', false)
  checkCognitoSession(dispatch).then(authUser => {
    if (authUser) {
      window.firebaseUser = authUser

      const params = new URLSearchParams(window.location.search)
      const stockPath = params.get('stockPath')
      if (stockPath && featureFlags.hasStockAppNav) {
        history.replaceState(null, '', window.location.pathname)
        window.location.href = getStockAppBaseUrl() + stockPath
        return
      }

      runAuthenticateFlow(dispatch, state, authUser)
    } else {
      redirectToSignin()
    }
  })
}

export default {
  start({ commit, dispatch, state }) {
    window.WiskGlobals = window.WiskGlobals || { currency: '$' }
    commit('mutateUIEditMode', !!getPreferences('uiEditModeActive'))
    dispatch('setDarkModeTheme', getPreferences('darkModeTheme', 2))

    if (getGlobalOptionsUnsubscribe) {
      getGlobalOptionsUnsubscribe()
    }

    dispatch('setTranslations')
    dispatch('getGlobalOptions').then(() => {
      setupAuthEvents(dispatch, state)
      dispatch('loadInfoTooltips')
      dispatch('getGridViewsCore')
    })
  },
  setDarkModeTheme({ commit }, payload) {
    darkModeMediaQuery.removeEventListener('change', handleColorSchemeChange)

    if (payload === 0) {
      applyLightMode()
    } else if (payload === 1) {
      applyDarkMode()
    } else if (payload === 2) {
      darkModeMediaQuery.addEventListener('change', handleColorSchemeChange)
      darkModeMediaQuery.matches ? applyDarkMode() : applyLightMode()
    }

    commit('mutateDarkModeTheme', payload)
  },
  loadInfoTooltips({ commit }) {
    commit('mutateInfoTooltipsById', infoTooltipsById)
  },
  saveInfoTooltip({ commit, state }, payload) {
    commit('mutateInfoTooltipsById', { ...state.infoTooltipsById, [payload.id]: payload })
    commit('mutateInfoTooltipsDirty', true)
  },
  downloadTooltipsFile({ state, commit }) {
    downloadObjectAsJson(state.infoTooltipsById, 'infoTooltips.json')
    commit('mutateInfoTooltipsDirty', false)
  },
  async reset({ commit, dispatch }) {
    commit('mutateResetState')
    dispatch('start')
  },
  setCurrentUser({ commit, dispatch }, user) {
    commit('mutateCurrentUser', user)
    dispatch('setCurrentPermissions')
    dispatch('setPageViewPermissions')
    setPreferences('currentUserId', user.id)
  },
  setCurrentVenue({ commit, dispatch, state }, { venue }) {
    window.WiskGlobals = window.WiskGlobals || {}
    venue.subscription = venue.subscription || {}

    if (venue.timezone && (state.user?.is_wisk_user || state.user?.god_mode)) {
      Settings.defaultZone = venue.timezone
      Settings.defaultLocale = state.user.language || 'en'

      Highcharts.setOptions({
        time: {
          timezone: venue.timezone
        }
      })
      window.WiskGlobals.timezoneDirty = true
    }

    window.WiskGlobals.venueBusinessDayStartHour = venue.business_day_starting_hour || 0

    if (typeof venue.last_locked_inventory_date === 'string') {
      venue.last_locked_inventory_date = new Date(venue.last_locked_inventory_date)
    }

    commit('mutateCurrentVenue', venue)
    dispatch('setCurrentFeatures')
    dispatch('setPageViewPermissions')

    if (!state.signupFlow && venue?.id && venue.id !== currentVenueId) {
      dispatch('getPOSItemsExtraData')
      dispatch('loadVenuesHighLevel')
      dispatch('getInventories', { count: 25, exclude_archived: true })
      dispatch('getShoppingCart')
      dispatch('getDistributorPrices')
      dispatch('getMovementExtraLineReasons')

      currentVenueId = venue.id
    }
  },
  async resetSyncProviderLocalPersistence({ state }, venue) {
    venue = venue || state.venue

    await cleanupDB(venue.id)

    if (venue.parent_venue_id) {
      await cleanupDB(venue.parent_venue_id)
    }

    await setTimeoutAsync(100)

    //refreshing the page
    window.location.replace('/')
  },
  getGlobalOptions({ commit, dispatch, state }) {
    return new Promise(resolve => {
      let resolved = false
      getGlobalOptionsUnsubscribe = subscribeToGlobalSettings(result => {
        if (result) {
          commit('mutateGlobalOptions', { ...result, loaded: true })

          let permissions = result.permissions || []
          permissions.sort((a, b) => compareNumbers(a.id, b.id))
          commit('mutatePermissions', permissions)

          if (state.user) {
            dispatch('setCurrentPermissions')
          }
          if (state.venue) {
            dispatch('setCurrentFeatures')
          }

          if (!resolved) {
            resolved = true
            resolve()
          }
        }
      }, 1_800_000) // 30 minutes
    })
  },
  setCurrentPermissions({ commit, state }) {
    let permissionIds = get(state, 'user.role.permissions', [])

    commit('mutateCurrentPermissions', permissionIds)
  },
  setCurrentFeatures({ commit, state }) {
    let features = get(state, 'venue.subscription.plan_features', []).map(f => ({ ...f })),
      selectedPlan = get(state, 'venue.subscription.selected_plan.type', ''),
      messages = get(state, 'options.plan_features_upgrade', {}),
      messagesByFeature = arrayToObjectById(messages[selectedPlan], 'feature'),
      allPossibleFeatures = [],
      featuresByType = {}

    Object.keys(messages).forEach(key => {
      if (Array.isArray(messages[key])) {
        messages[key].forEach(m => {
          allPossibleFeatures.push(m.feature)
        })
      }
    })

    allPossibleFeatures = uniqWith(allPossibleFeatures, isEqual)

    features.forEach(feature => {
      let found = messagesByFeature[feature.feature_identifier]
      feature.message = (found && found.message) || ''
      feature.enabled = true
    })

    featuresByType = arrayToObjectById(features, 'feature_identifier')

    allPossibleFeatures.forEach(featureType => {
      if (!featuresByType[featureType]) {
        featuresByType[featureType] = { enabled: false }

        let found = messagesByFeature[featureType]
        featuresByType[featureType].message = (found && found.message) || ''
      }
    })
    commit('mutateCurrentFeatures', featuresByType)
  },
  setPageViewPermissions({ commit, state, getters }) {
    if (state.user && state.venue && state.currentPermissionsByType) {
      let godMode = !!state.user.god_mode,
        venue = state.venue,
        deniedAllTopItems = venue?.subscription?.status === 'delinquent' || !state?.user?.user_venues?.length,
        deniedAllUserItems = false,
        permissions = {
          initDone: true,
          all: godMode,
          top: godMode || !deniedAllTopItems,
          userItems: godMode || !deniedAllUserItems,
          //top items
          venues: godMode,
          venue_transfers: !!venue.multivenue_enabled,
          dashboard: !!state.currentPermissionsByType.dashboard_view || godMode,
          venue_bottles: !!state.currentPermissionsByType.item_view || godMode,
          venue_bottles_recent: !!state.currentPermissionsByType.item_view || godMode,
          venue_bottles_check_units: !!state.currentPermissionsByType.item_view || godMode,
          distributors: !!state.currentPermissionsByType.item_view || godMode,
          wisk_distributors: godMode,
          master_item_review: godMode,
          families: !!state.currentPermissionsByType.item_view || godMode,
          inventories: !!state.currentPermissionsByType.inventory_view || godMode,
          independent_inventories: !!state.currentPermissionsByType.independent_inventory_view || godMode,
          reports: !!state.currentPermissionsByType.inventory_view || godMode,
          consumption: !!state.currentPermissionsByType.consumption_view || godMode,
          orders: !!state.currentPermissionsByType.purchase_order_view || godMode,
          purchase_orders: !!state.currentPermissionsByType.purchase_order_view || godMode,
          intakes: !!state.currentPermissionsByType.invoice_view || godMode,
          scanned_invoices: !!state.currentPermissionsByType.invoice_view || godMode,
          requisitions: getters.belongsToOutletVenue || godMode,
          stockroom: getters.belongsToCentralStockroomVenue || godMode,
          transfers: !!state.currentPermissionsByType.invoice_view || godMode,
          movements_summary: !!state.currentPermissionsByType.invoice_view || godMode,
          adjustments: !!state.currentPermissionsByType.invoice_view || godMode,
          sales: !getters.isCentralStockroom && (!!state.currentPermissionsByType.sale_view || godMode),
          sales_test: godMode,
          pos_items: !getters.isCentralStockroom && (!!state.currentPermissionsByType.pos_item_view || godMode),
          sub_recipes: !!state.currentPermissionsByType.batch_view,
          variance: !getters.isCentralStockroom && (!!state.currentPermissionsByType.variance_view || godMode),
          variance_per_month: !getters.isCentralStockroom && venue.enable_variance_for_month && (!!state.currentPermissionsByType.variance_view || godMode),
          price_changes: !!state.currentPermissionsByType.invoice_view || godMode,
          stock_analysis: !!state.currentPermissionsByType.item_view || godMode,
          cost_changes: !!state.currentPermissionsByType.inventory_view || godMode,
          download_manager: !!state.currentPermissionsByType.invoice_view || godMode,
          historical_item_stock: (!!state.currentPermissionsByType.item_view && venue.show_historical_real_time_stock) || godMode,
          property: venue.is_part_of_hotel && (!!state.currentPermissionsByType.inventory_view || godMode),

          multi_venue_analytics: (venue.organization_id && state.venuesHighLevel.filter(v => v.organization_id === venue.organization_id).length > 2) || godMode,

          //groups
          group_items: true, //will be hidden if permissions leave it empty
          group_inventories: true, //will be hidden if permissions leave it empty
          group_orders: true, //will be hidden if permissions leave it empty
          group_reports: true, //will be hidden if permissions leave it empty
          group_sales: true, //will be hidden if permissions leave it empty
          group_transfers: true, //will be hidden if permissions leave it empty

          //user items
          help: !godMode,
          account: true,
          reload: true,
          team: true,
          subscription: !!state.venue.stripe_publishable_key && !!state.venue.stripe_id && (!!state.currentPermissionsByType.billing_manage || godMode),
          venue_settings: !!state.currentPermissionsByType.venue_settings_manage || godMode,
          reload_local_venue_data: !state.currentPermissionsByType.venue_settings_manage,
          deep_link: true,
          locations: !!state.currentPermissionsByType.area_manage || godMode,
          venue_switch: !!state.user.user_venues,
          venue_onboarding: true,
          auth: true,
          testpage: true,
          organizations: godMode,
          components_demo: true,
          translations: true,
          public_order: true,
          public_no_credit_card_email: true,
          public_signup_lightspeed: true,
          public_lightspeed_signup: true,
          public_redirect: true,
          signin: true,
          signup: true,
          logout: true,
          login_demo: true,
          dashboard_printable: true,
          churned: !state.user.god_mode && venue?.subscription?.status === 'churned',
          'not-found': true
        }
      commit('mutatePageViewPermissions', permissions)
    }
  },
  handleReturnUrl({ state }) {
    // stockPath — cross-app: redirect to wisk-stock after signin
    // returnUrl — internal: redirect within wisk-vuejs after signin
    const params = new URLSearchParams(window.location.search)
    const stockPath = params.get('stockPath') || state?.route?.query?.stockPath
    const returnUrl = params.get('returnUrl') || state?.route?.query?.returnUrl

    if (stockPath && featureFlags.hasStockAppNav) {
      history.replaceState(null, '', window.location.pathname)
      window.location.href = getStockAppBaseUrl() + stockPath
      return
    }

    if (!returnUrl) return

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const { returnUrl: _ru, stockPath: _sp, ...rest } = state?.route?.query || {}
    router.replace({ path: returnUrl, query: rest }).catch(() => {})
    setTimeout(() => {
      venueIdToUrl(state.venue.id)
    }, 0)
  },
  logout({ dispatch, commit }) {
    localStorage.removeItem('X-venue_id')
    venueIdToUrl(null)
    dispatch('setFirebaseUser', null)
    commit('mutateCurrentUser', null)
    commit('mutateCurrentVenue', null)
    setPreferences('currentUserId', null)
    return authSignOut()
  },
  setFirebaseUser({ commit }, payload) {
    commit('mutateFirebaseUser', payload)
  },
  cognitoSignInComplete({ dispatch, state }, authUser) {
    window.firebaseUser = authUser
    dispatch('setFirebaseUser', authUser)
    return runAuthenticateFlow(dispatch, state, authUser)
  },
  setWaitingForVenue({ commit }, payload) {
    commit('mutateWaitingForVenue', payload)
  },
  setSignupFlow({ commit }, payload) {
    commit('mutateSignupFlow', payload)
  },
  setWindowResizeKey({ commit }, payload) {
    commit('mutateWindowResizeKey', payload)
  },
  setDeviceInfo({ commit }, payload) {
    commit('mutateDeviceInfo', payload)
  },
  async setUser({ dispatch, commit, state }, payload) {
    let user = await api.updateUser(payload.id, payload.operation, !!state.signupFlow)

    if (user && state.user && payload.id === state.user.id && !payload.preventLoadConfig) {
      dispatch('setCurrentUser', user)
    }

    commit('mutateWaitingForVenue', payload.preventLoadConfig)

    return user
  },
  refreshCurrentUser({ dispatch, state }) {
    api
      .authenticate()
      .then(wiskUser => {
        if (wiskUser) {
          if (wiskUser?.venue?.id && !state.signupFlow) {
            dispatch('setCurrentVenue', { venue: wiskUser.venue, caller: 'authenticate wiskUser.venue' })
          } else {
            dispatch('setCurrentVenue', { venue: { id: 0, subscription: {} }, caller: 'authenticate no venue yet' })
            setTimeout(() => {
              dispatch('setLoadingScreenVisible', false)
            }, 500)
          }
        }
        dispatch('setCurrentUser', wiskUser)
      })
      .catch(() => {})
  },
  changeActiveVenue(z, { venueId, userId }) {
    api.venueLogin(venueId, userId)

    window.location.reload()
    // dispatch('reset')
  },
  setSidebarMinimized({ commit }, payload) {
    commit('mutateSidebarMinimized', payload)
  },
  refreshView({ commit, state }) {
    commit('mutateViewRefreshKey', state.viewRefreshKey + 1)
  },
  async setTranslations({ commit, state }) {
    let currency = window?.WiskGlobals?.currency || '$',
      languageKey = get(state, 'user.language', navigator.language.substring(0, 2) || 'EN').toUpperCase(),
      copy = cloneDeep(translations)

    Object.keys(copy).forEach(key => {
      if (typeof copy[key] === 'string' && copy[key].includes('$')) {
        copy[key] = copy[key].replace(new RegExp('\\$', 'g'), currency)
      }
    })
    if (!['en', 'fr', 'es'].includes(languageKey.toLowerCase())) {
      languageKey = 'EN'
    }
    const translationsByLanguage = await import(`../modules/translations/${languageKey.toLowerCase()}.js`)
    copy = merge({}, copy, translationsByLanguage.default)
    copy.translate = translations.translate.bind(translationsByLanguage.default)
    commit('mutateTranslations', copy)
    setTimeout(() => {
      document.querySelector('.mainContainer')?.classList.add('translations-ready')
    }, 2000)
  },
  async setLoadingScreenVisible({ commit, state }, payload) {
    commit('mutateLoadingScreenVisibility', get(state, 'route.meta.lightWeightPage') ? false : !!payload)
  },
  async setInitialDBLoadingProgress({ commit }, payload) {
    commit('mutateInitialDBLoadingProgress', payload)
  },
  setInitialDataLoadComplete({ commit }, payload) {
    commit('mutateInitialDataLoadComplete', payload)
  },
  notify({ commit }, payload) {
    commit('mutateNotification', payload)
  },
  async getMovements({ commit }) {
    let movements = await api.movements()
    movements.forEach(movement => {
      if (movement?.date) {
        movement.date = new Date(movement.date)
      }
    })
    movements.sort((a, b) => compareNumbers(b.date.getTime(), a.date.getTime()))

    commit('mutateMovements', movements)
  },
  async getMovementExtraLineReasons({ commit }) {
    let reasons = await api.movementExtraLineReasons()
    reasons.sort((a, b) => compareNumbers(a.id, b.id))

    commit('mutateMovementExtraLineReasons', reasons)
  },
  async setMovement({ commit }, payload) {
    let movement = await api.updateMovement(payload.id, payload.operation)
    if (movement?.date) {
      movement.date = new Date(movement.date)
    }

    commit('mutateMovement', movement)
    return movement
  },
  emailVerified(store, uid) {
    api.emailVerified(uid).finally(() => {
      window.location.href = '/'
    })
  },
  async setInventory({ commit }, payload) {
    let updatedInventory = await api.updateInventory(payload.id, payload.operation)

    commit('mutateInventory', updatedInventory)

    return updatedInventory
  },
  async openActiveInventory({ dispatch }) {
    let result = await api.startInventory({ started_at: new Date().toISOString() })
    if (result?.current?.id) {
      dispatch('setGlobalAction', { type: 'inventoryEdit', action: { id: result.current.id } })
    }
  },
  async getInventories({ commit }, payload) {
    let inventories = await api.inventoriesHighLevel(payload)

    inventories.forEach(inventory => {
      if (inventory && (inventory.date || inventory.started_at)) {
        inventory.date = new Date(inventory.date || inventory.started_at)
        inventory.started_at = inventory.date
      }
      if (inventory?.finished_at) {
        inventory.finished_at = new Date(inventory.finished_at)
      }
    })
    inventories.sort((a, b) => compareNumbers(b.date.getTime(), a.date.getTime()))

    if (!payload?.count) {
      commit('mutateInventoriesHighLevelLoadAllComplete', true)
    }
    commit('mutateInventories', inventories)
  },
  async loadVenues({ commit }) {
    let venues = await api.venues()
    commit('mutateVenues', venues)
  },
  async loadVenuesHighLevel({ commit }) {
    let venuesHighLevel = await api.venuesHighLevel()

    if (Array.isArray(venuesHighLevel)) {
      venuesHighLevel.sort((a, b) => compareNumbers(a.title, b.title))
      commit('mutateVenuesHighLevel', venuesHighLevel)
    }
  },
  async getDistributorPrices({ commit }) {
    commit('mutateDistributorPrices', await api.getDistributorPrices())
  },
  async updateVenue({ commit }, payload) {
    let updated = await api.updateVenue(payload.id, payload.operation)

    commit('mutateVenue', updated)

    return updated
  },
  setCustomField(z, payload) {
    api.updateCustomField(payload.id, payload.operation)
  },
  setGridView({ commit }, payload) {
    let operation = payload.operation,
      data = operation.value,
      id = payload.id

    if (operation.type === 'clear') {
      delete operation.value

      data = { id, REMOVE: true }
    }

    commit('mutateGridView', data)

    api.updateWebView(id, operation)
  },
  setGridViewCore({ commit, dispatch, state }, payload) {
    let operation = payload.operation,
      data = operation.value,
      id = payload.id

    if (operation.type === 'clear') {
      delete operation.value

      data = { id, REMOVE: true }
    }

    if (state?.user?.god_mode) {
      console.warn('setGridViewCore - this is only local save in memory, from here the user can download JSON if godmode, open editor, etc', data)
      dispatch('notify', { type: 'info', message: 'Grid view saved to state. System views are not sent to API, only saved in JSON' })
    } else {
      dispatch('notify', { type: 'info', message: state.translations.txtWiskGridViewModificationDisabledMessageSystem })
    }

    commit('mutateGridViewCore', data)
  },
  getGridViewsCore({ commit }) {
    commit('mutateGridViewsCore', gridViewsCore)
  },
  setCustomFiledsByTarget({ commit, getters, state }) {
    if (state.customFields?.length) {
      let result = {}
      for (let i = 0; i < state.customFields.length; i++) {
        let field = state.customFields[i]
        field.type_definition = field.type_definition || {}

        if (field?.type_definition?.type) {
          field.inputTypeAttrs = {
            ...getters.customFieldsTypeTranslations[field.type_definition.type],
            prefix: field.type_definition.symbol,
            label: field.label,
            operation: 'custom_field',
            operationEmpty: 'custom_field_clear',
            items: field.type_definition.type === 'dropdown' ? prepareItemsForWiskInput(field.type_definition.values) : field.type_definition.values,
            customFieldValueWrapper: {
              id: field.id,
              value: {
                type: field.type_definition.type,
                value: null
              }
            }
          }
        }

        if (isFinite(field.type_definition.decimal_places)) {
          field.inputTypeAttrs.decimals = field.type_definition.decimal_places
        }
        if (field.inputTypeAttrs.multiple) {
          field.inputTypeAttrs.callBeforeAddTag = title =>
            new Promise(resolve => {
              let value = { uuid: guid(), title },
                operation = {
                  type: 'type_definition',
                  value: {
                    ...field.type_definition,
                    values: [...field.type_definition.values, value]
                  }
                }

              api.updateCustomField(field.id, operation).then(() => {
                resolve({ ...value })
              })
            })
        }

        result[field.item_type] = result[field.item_type] || []
        result[field.item_type].push(field)
      }

      commit('mutateCustomFieldsByTarget', result)

      let customFieldsByTargetActive = merge({}, state.customFieldsByTarget)

      Object.keys(customFieldsByTargetActive).forEach(key => {
        if (Array.isArray(customFieldsByTargetActive[key])) {
          customFieldsByTargetActive[key] = customFieldsByTargetActive[key].filter(c => c && !c.archived)
        }
      })

      commit('mutateCustomFieldsByTargetActive', customFieldsByTargetActive)
    }
  },
  async updateVenueTransfer(z, payload) {
    let updated = await api.updateVenueTransfer(payload.id, payload.operation)

    return updated
  },
  async getShoppingCart({ commit }) {
    let shoppingCart = await api.getShoppingCart()
    commit('mutateShoppingCart', shoppingCart)
  },
  async addShoppingCart({ dispatch, state, getters }, operation) {
    let newValue = operation.units,
      shoppingCartItem = state.shoppingCart.find(i => i.item_distributor_id === operation.item_distributor_id),
      oldValue = (shoppingCartItem && shoppingCartItem.units) || 0,
      distributor_id = state.itemVariantsById[operation.item_distributor_id].distributor_id

    if (getters.hasCentralStockroom) {
      distributor_id = getters.distributorLinkedToCentralStockroom.id
    }

    let updatedOperation = {
      item_distributor_id: operation.item_distributor_id,
      manual_source: operation.manual_source,
      distributor_id,
      units: (operation.type && operation.units) || (newValue > oldValue ? newValue - oldValue : oldValue - newValue),
      type: operation.type || (newValue > oldValue ? 'add' : 'substract')
    }
    try {
      let currentShoppingCart = await api.addToShoppingCart(updatedOperation)
      dispatch('updateShoppingCart', currentShoppingCart)
    } catch (error) {
      logEvent.error({
        message: 'add shoping cart: error adding item to cart',
        metadata: {
          ...extractErrorMetadata(error),
          venueId: state.venue.id,
          operation: JSON.stringify(updatedOperation)
        }
      })
      if (error?.data?.code === 400) {
        dispatch('notify', {
          type: 'makeToast',
          text: state.translations.txtCartBuilderItemMissingInDistributor,
          timeout: false,
          innerType: 'error'
        })
        dispatch('setGlobalAction', {
          action: { distributorId: distributor_id, byLinkedVenue: true, itemDistributorId: operation.item_distributor_id },
          type: 'itemsForDistributor'
        })
      }
    }
  },
  updateShoppingCart({ commit }, shoppingCart) {
    if (shoppingCart) {
      shoppingCart = shoppingCart.filter(item => !!item.units)
      commit('mutateShoppingCart', shoppingCart)
    }
  },
  async clearShoppingCart({ dispatch }, payload) {
    let updated = await api.clearShoppingCart(payload)

    dispatch('updateShoppingCart', updated)
  },
  async clearShoppingCartAll({ dispatch }, payload) {
    let updated = await api.clearShoppingCartAll(payload)

    dispatch('updateShoppingCart', updated)
  },
  async checkoutDistributors({ dispatch }, distributorsList) {
    let result = await api.checkoutDistributors(distributorsList)

    dispatch('updateShoppingCart', result.shopping_cart)
    await dispatch('getPurchaseOrders')

    return result.purchase_orders[0]
  },
  async prefillDistributor({ dispatch }, payload) {
    let result = await api.prefillDistributor(payload)
    dispatch('updateShoppingCart', result)
  },
  async getPurchaseOrders({ commit }, filter) {
    let orders = await api.getPurchaseOrders(filter)
    orders.forEach(order => {
      if (order && (order.date || order.started_at)) {
        order.date = new Date(order.date || order.started_at)
        order.started_at = order.date
      }
    })
    orders.sort((a, b) => compareNumbers(b.date.getTime(), a.date.getTime()))
    commit('mutateOrders', orders)
  },
  async setPurchaseOrder({ commit, dispatch }, payload) {
    let order = await api.updatePurchaseOrder(payload.id, payload.operation)
    if (order && (order.date || order.started_at)) {
      order.date = new Date(order.date || order.started_at)
      order.started_at = order.date
    }
    commit('mutateOrder', order)
    // Re-fetching restores those fields so the grid total column stays correct.
    dispatch('getPurchaseOrders')
    return order
  },
  async intakeFromOrder({ commit }, payload) {
    let result = await api.intakeFromOrder(payload.orderId, payload.data)

    if (result?.purchase_order?.date) {
      result.purchase_order.date = new Date(result.purchase_order.date)
    }

    commit('mutateOrder', result.purchase_order)

    if (result?.movement?.date) {
      result.movement.date = new Date(result.movement.date)
    }
    commit('mutateMovement', result.movement)
    return result.movement
  },
  async setSubrecipe(z, payload) {
    let updatedSubrecipe = await api.updateSubrecipe(payload.id, payload.operation)

    return updatedSubrecipe
  },
  async getPOSItemsExtraData({ commit, dispatch, getters, state }, params) {
    if (!getters.configDataLoaded) {
      await setTimeoutAsync(1000)
      return dispatch('getPOSItemsExtraData', params)
    }
    if (!state.currentPermissionsByType.pos_item_view) {
      return []
    }

    let extraData = await api.getPOSItemsExtraData(params)

    commit('mutatePOSItemsExtraData', extraData)

    return extraData
  },
  async setPOSItem({ commit }, payload) {
    let updatedPOSItem = await api.updatePOSItem(payload.id, payload.operation)

    commit('mutatePOSItem', updatedPOSItem)

    return updatedPOSItem
  },
  async updateServingSize(z, payload) {
    let updatedServingSize = await api.updateServingSize(payload.id, payload.operation)
    updatedServingSize.type = 'predefined'
    //  commit('mutateServingSize', updatedServingSize)
    return updatedServingSize
  },
  async setServingSizesOrder(z, payload) {
    let updatedServingSizes = await api.setServingSizesOrder(payload)
    return updatedServingSizes
  },
  setItemToState({ commit }, payload) {
    commit('mutateBottle', payload)
  },
  async importItem({ commit }, payload) {
    let item = await api.bottleImport(payload)

    if (item) {
      commit('mutateBottle', item)

      if (item.item_distributors && item.item_distributors[0]) {
        commit('mutateItemVariant', item.item_distributors[0])
      }
    }
    return item
  },
  async updateBottle({ commit }, payload) {
    let updatedBottle = await api.updateBottle(payload.id, payload.operation)

    if (updatedBottle && !payload.id) {
      commit('mutateBottle', updatedBottle)

      if (updatedBottle.item_distributors && updatedBottle.item_distributors[0]) {
        commit('mutateItemVariant', updatedBottle.item_distributors[0])
      }
    }
    return updatedBottle
  },
  async setItemVariant(z, payload) {
    let updated = await api.saveItemVariant(payload.id, payload.operation)

    return updated
  },
  async setLocation(z, payload) {
    let updatedLocation = await api.updateLocation(payload.id, payload.operation)
    return updatedLocation
  },
  async importBottle(z, payload) {
    let updatedBottle = await api.bottleImport(payload)
    //commit('mutateBottle', updatedBottle)
    return updatedBottle
  },
  async setDistributor({ commit }, payload) {
    let distributor = await api.updateDistributor(payload.id, payload.operation)

    commit('mutateDistributor', distributor)
    return distributor
  },
  async setCategory(z, payload) {
    let response = await api.updateCategory(payload.id, payload.operation)

    return response
  },
  async setFamily(z, payload) {
    let response = await api.updateFamily(payload.id, payload.operation)

    return response
  },
  toggleUIEditMode({ commit, state }) {
    commit('mutateUIEditMode', !state.uiEditModeActive)
  },
  getWiskModalNextLayer({ commit, state }) {
    return new Promise(resolve => {
      let topLayer = { id: guid(), zIndex: 1100 },
        wiskModalLayers = merge([], state.wiskModalLayers)

      modalCounter++

      if (!(modalCounter % 25)) {
        topLayer.smile = true
      }
      if (wiskModalLayers.length) {
        topLayer.zIndex = wiskModalLayers[wiskModalLayers.length - 1].zIndex + 10
      }
      topLayer.count = wiskModalLayers.length
      wiskModalLayers.push(topLayer)
      commit('mutateWiskModalTopLayer', wiskModalLayers)
      resolve(topLayer)
    })
  },
  releaseWiskModalLayer({ commit, state }) {
    let wiskModalLayers = merge([], state.wiskModalLayers)

    if (wiskModalLayers.length) {
      wiskModalLayers.splice(wiskModalLayers.length - 1, 1)
    }
    commit('mutateWiskModalTopLayer', wiskModalLayers)
  },
  async subscribeToRealtimeSync({ commit, state, dispatch }) {
    await initDB({ commit, state, dispatch })
    try {
      const userQuery = liveQueryHandler({ type: 'users_venues_pivot', key: 'user_id', payload: state.user.id })
      let roleSubscription
      let activeRoleId

      userQuery.subscribe({
        next: userVenue => {
          const nextRole = userVenue[0]?.role || state.user.role
          dispatch('setCurrentUser', { ...state.user, role: nextRole })

          const roleId = nextRole?.id

          if (!roleId || roleId === activeRoleId) {
            return
          }

          activeRoleId = roleId
          if (roleSubscription?.unsubscribe) {
            roleSubscription.unsubscribe()
          }

          const roleQuery = liveQueryHandler({ type: 'role', key: 'role_id', payload: roleId })
          roleSubscription = roleQuery.subscribe({
            next: role => {
              dispatch('setCurrentUser', { ...state.user, role: role[0] })
            },
            error: error => console.error(error)
          })
        },
        error: error => console.error(error)
      })
    } catch (error) {
      console.error('roleQuery error', error)
    }
  },
  setGlobalAction({ commit, getters, dispatch, state }, payload) {
    let copy = merge({}, payload)

    const permissionMap = {
      inviteUsers: 'user_add',
      independentInventoryEdit: 'independent_inventory_view',
      families: 'family_category_view',
      familyEdit: 'family_category_view',
      categoryEdit: 'family_category_view',
      movementEdit: 'invoice_manage',
      glAccountEdit: 'gl_account_manage',
      taxEdit: 'tax_rate_manage',
      itemEdit: 'item_view',
      itemDetails: 'item_view',
      posItemEdit: 'pos_item_view',
      posModifierEdit: 'pos_item_view',
      subrecipeEdit: 'batch_view',
      planSelectorEdit: 'billing_manage',
      parentVenueItems: 'shared_item_manage',
      venueTransferEdit: 'transfer_request_manage',
      venueEdit: 'venue_settings_manage'
    }
    if (copy?.action) {
      copy.action.key = guid()
    }
    if (copy?.caller) {
      console.log('+-+-+-+-+-+-+-+-+-+-+-+-+ setGlobalAction caller', copy.caller, copy)
    }

    if (!copy.skipPermissionCheck && (isEmpty(state.currentPermissionsByType) || !getters.configDataLoaded)) {
      setTimeout(() => {
        //permissions not here yet, come back a bit later
        dispatch('setGlobalAction', copy)
      }, 1000)
    } else if (!permissionMap[copy.type] || (permissionMap[copy.type] && state.currentPermissionsByType[permissionMap[copy.type]])) {
      if (copy.resetBeforeOpen) {
        commit('mutateGlobalAction', { type: copy.type, action: null })

        setTimeout(() => {
          commit('mutateGlobalAction', copy)
        }, 0)
      } else {
        commit('mutateGlobalAction', copy)
      }
    }
  },
  handleMultiChanges({ commit, state }, { type, dataMapper, transformer }) {
    if (debug) {
      console.time(`Performance investigation * handleMultiChanges before commit ${type}`)
      console.log(`Performance investigation * dataMapper for ${type}`, dataMapper)
    }
    let stateData
    if (dataMapper.stateAsCollector) {
      stateData = Object.keys(state[dataMapper.state]).reduce((acc, key) => acc.concat(state[dataMapper.state][key]), [])
    } else if (Array.isArray(state[dataMapper.state])) {
      stateData = [...state[dataMapper.state]]
      // handle merging draft pos items into pos items statically
      if (dataMapper.type === 'pos_item') {
        stateData.push(...state.draftPOSItems)
      }
    } else {
      stateData = Object.values(state[dataMapper.state])
    }
    if (debug) {
      console.log(`Performance investigation * current stateData for ${type}`, [...stateData])
    }
    dataMapper.deletedCollector.forEach(item => {
      const index = stateData.findIndex(i => (i && typeof i === 'object' ? i[dataMapper.primaryKey || 'id'] : i) === item)
      if (index > -1) {
        stateData.splice(index, 1)
      }
    })
    dataMapper.collector.forEach(item => {
      const index = stateData.findIndex(i => (i && typeof i === 'object' ? i[dataMapper.primaryKey || 'id'] : i) === item.data[dataMapper.primaryKey || 'id'])
      if (index === -1) {
        stateData.push(transformer ? transformer(item.data) : item.data)
      } else {
        stateData[index] = { ...(transformer ? transformer(item.data) : item.data) }
      }
    })
    if (debug) {
      console.log(`Performance investigation * new stateData for ${type}, before commit`, [...stateData])
    }
    commit(dataMapper.multi, stateData)
    if (dataMapper.afterCommit) {
      dataMapper.afterCommit(dataMapper.collector)
    }
    if (debug) {
      console.timeEnd(`Performance investigation * handleMultiChanges before commit ${type}`)
    }
  }
}
